import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, PaginationQueryWithFields, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { LeasingCompany, LeasingCompanyVersion, UpsertLeasingCompany } from "./types";

export const useLeasingCompaniesApi = () => {
  const api = useApi();

  const getAdminLeasingCompany = useCallback(
    (leasingCompanyId: string, signal: AbortSignal) => {
      const url = `/admin/leasing_companies/${leasingCompanyId}`;
      return useSWRMutation<LeasingCompany | null, CustomError, string>(url, (url) =>
        api.get<LeasingCompany>({ url, signal })
      );
    },
    [api]
  );

  const useAdminLeasingCompanies = useCallback(
    <K extends keyof LeasingCompany>(
      query: PaginationQueryWithFields<Pick<LeasingCompany, K>>,
      signal: AbortSignal,
      hasPermission?: boolean
    ) => {
      const url = buildUrl<Pick<LeasingCompany, K>>("/admin/leasing_companies", query);

      const key = hasPermission === false ? false : url;

      return useSWR<Paginated<Pick<LeasingCompany, K>> | null, CustomError, string | null>(
        key as string | null,
        (url: string) => api.get<Paginated<Pick<LeasingCompany, K>>>({ url, signal })
      );
    },
    [api]
  );

  const insertAdminLeasingCompany = useCallback(
    async (body: UpsertLeasingCompany): Promise<{ id: string }> => {
      const url = `/admin/leasing_companies`;
      const id = await api.post<UpsertLeasingCompany, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api]
  );

  const updateAdminLeasingCompany = useCallback(
    async (leasingCompanyId: string, body: UpsertLeasingCompany): Promise<void> => {
      const url = `/admin/leasing_companies/${leasingCompanyId}`;
      await api.put<UpsertLeasingCompany>(url, body),
        api.mutateCache([
          "/admin/leasing_companies",
          "/admin/car_leasings",
          `/admin/leasing_companies/${leasingCompanyId}/versions`,
        ]);
    },
    [api]
  );

  const deleteAdminLeasingCompany = useCallback(
    async (leasingCompanyId: string): Promise<void> => {
      const url = `/admin/leasing_companies/${leasingCompanyId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/leasing_companies`]);
      });
    },
    [api]
  );

  const getAdminLeasingCompanyHistory = useCallback(
    (query: PaginationQuery, leasingCompanyID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/leasing_companies/${leasingCompanyID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal })
      );
    },
    [api]
  );

  const getAdminLeasingCompanyVersion = useCallback(
    (leasingCompanyId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/leasing_companies/${leasingCompanyId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<LeasingCompanyVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<LeasingCompanyVersion>>({ url, signal })
      );
    },
    [api]
  );

  return {
    getAdminLeasingCompany,
    useAdminLeasingCompanies,
    insertAdminLeasingCompany,
    updateAdminLeasingCompany,
    deleteAdminLeasingCompany,
    getAdminLeasingCompanyHistory,
    getAdminLeasingCompanyVersion,
  };
};
